/* eslint-disable max-lines */
import {
  useAddPersonMatchProfile,
  useAddPersonProfileLocationMapping,
  useCheckPersonProfileImageQuality,
  useDeletePersonProfiles,
  useUpdatePersonMatchProfile,
  useUploadPersonProfileImage,
} from '@hakimo-ui/hakimo/data-access';
import {
  PersonMatchProfile,
  ProfileFormState,
  UpdatePersonMatchProfilePayload,
  ValidationResult,
} from '@hakimo-ui/hakimo/types';
import { toast, useUser } from '@hakimo-ui/hakimo/util';
import { Button, Modal } from '@hakimo-ui/shared/ui-base';
import { useId, useState, useEffect } from 'react';
import {
  INITIAL_FORM_STATE,
  ADD_PROFILE_ID_PREFIX,
  MIN_AGE,
  validateProfileForm,
  validateTenantAccess,
  createValueUpdater,
} from './utils';
import {
  ProfileFormFields,
  ProfileImageUpload,
  ModalAlerts,
} from './ProfileModalComponents';

interface Props {
  onClose: () => void;
  isEditing: boolean;
  profile?: PersonMatchProfile;
  locationId?: number;
  tenantId?: string;
  showBadgeId?: boolean;
  readOnly?: boolean;
}

export function AddEditProfileModal(props: Props) {
  const {
    onClose,
    isEditing,
    profile,
    locationId,
    tenantId,
    showBadgeId = false,
    readOnly = false,
  } = props;
  const user = useUser();
  const id = useId();
  const [formState, setFormState] = useState<ProfileFormState>(
    isEditing && profile
      ? {
          id: profile.id,
          first_name: profile.firstName || '',
          last_name: profile.lastName || '',
          age:
            profile.age && profile.age > MIN_AGE ? profile.age.toString() : '',
          gender: profile.gender,
          employee_id: profile.employeeId || '',
          badge_id: profile.badgeId || '',
          is_enabled: profile.isEnabled ?? false,
          profile_type: profile.profileType || 'BLACKLISTED',
        }
      : {
          ...INITIAL_FORM_STATE,
          id: ADD_PROFILE_ID_PREFIX + id,
        }
  );

  const [imagePreview, setImagePreview] = useState<string | null>(
    isEditing && profile ? profile.primaryImageURL || null : null
  );
  const [secondaryImagePreview, setSecondaryImagePreview] = useState<
    string | null
  >(
    isEditing &&
      profile &&
      profile.secondaryImageURL &&
      profile.secondaryImageURL.trim() !== ''
      ? profile.secondaryImageURL
      : null
  );
  const [fullBodyImagePreview, setFullBodyImagePreview] = useState<
    string | null
  >(
    isEditing &&
      profile &&
      profile.fullBodyImageURL &&
      profile.fullBodyImageURL.trim() !== ''
      ? profile.fullBodyImageURL
      : null
  );

  const [validationState, setValidationState] = useState<ValidationResult>({
    isValid: true,
    message: '',
  });

  const [createdProfileId, setCreatedProfileId] = useState<string | null>(null);
  const [profileSaved, setProfileSaved] = useState<boolean>(false);
  const [uploadsInitiated, setUploadsInitiated] = useState<boolean>(false);
  const [checkingQuality, setCheckingQuality] = useState<boolean>(false);

  const onImageUploadSuccess = (label: string) => () => {
    toast(`${label} image uploaded successfully`, { type: 'success' });
  };

  const onImageUploadError = (label: string) => (err: Error) => {
    toast(
      `${label} image upload failed${err.message ? `: ${err.message}` : ''}`,
      { type: 'error' }
    );
  };

  const onProfileSuccess = (data?: any) => {
    toast(
      `Face recognition profile ${
        isEditing ? 'updated' : 'added'
      } successfully`,
      { type: 'success' }
    );

    if (!isEditing && data) {
      const profileId = data?.personProfileId ?? data?.id ?? null;
      if (profileId) {
        setCreatedProfileId(profileId);
        if (locationId) {
          locationMappingMutation.mutate({
            personProfileId: profileId,
            locationId: locationId,
          });
        }
      }
    }
    setProfileSaved(true);
  };

  const locationMappingMutation = useAddPersonProfileLocationMapping(
    undefined,
    (err: Error) => {
      toast(`Error mapping profile to location: ${err.message}`, {
        type: 'error',
      });
    }
  );

  const primaryQualityMutation = useCheckPersonProfileImageQuality('primary');
  const secondaryQualityMutation =
    useCheckPersonProfileImageQuality('secondary');

  const deleteProfilesMutation = useDeletePersonProfiles(undefined, () => {
    /* swallow rollback errors; user already saw the primary upload error */
  });

  const imageUploadMutation = useUploadPersonProfileImage(
    isEditing && profile ? profile.id : createdProfileId || '',
    onImageUploadSuccess('Primary'),
    onImageUploadError('Primary'),
    'primary'
  );
  const secondaryImageUploadMutation = useUploadPersonProfileImage(
    isEditing && profile ? profile.id : createdProfileId || '',
    onImageUploadSuccess('Secondary'),
    onImageUploadError('Secondary'),
    'secondary'
  );
  const fullBodyImageUploadMutation = useUploadPersonProfileImage(
    isEditing && profile ? profile.id : createdProfileId || '',
    onImageUploadSuccess('Full body'),
    onImageUploadError('Full body'),
    'full_body'
  );

  const addMutation = useAddPersonMatchProfile(onProfileSuccess);
  const editMutation = useUpdatePersonMatchProfile(
    profile?.id || '',
    onProfileSuccess
  );

  useEffect(() => {
    if (!profileSaved || uploadsInitiated) return;
    const targetProfileId =
      isEditing && profile ? profile.id : createdProfileId;
    if (!targetProfileId) return;

    setUploadsInitiated(true);

    const uploadSequentially = async () => {
      if (formState.primary_image) {
        const formData = new FormData();
        formData.append('file', formState.primary_image);
        try {
          await imageUploadMutation.mutateAsync(formData);
        } catch {
          if (!isEditing && createdProfileId) {
            try {
              await deleteProfilesMutation.mutateAsync({
                personProfileIds: [createdProfileId],
              });
            } catch {
              // rollback errors are swallowed by the hook's onError
            }
            setCreatedProfileId(null);
          }
          setProfileSaved(false);
          setUploadsInitiated(false);
          return;
        }
      }

      const remainingUploads = [
        {
          file: formState.secondary_image,
          mutation: secondaryImageUploadMutation,
        },
        {
          file: formState.full_body_image,
          mutation: fullBodyImageUploadMutation,
        },
      ].filter((upload) => !!upload.file);

      for (const upload of remainingUploads) {
        const formData = new FormData();
        formData.append('file', upload.file as File);
        try {
          await upload.mutation.mutateAsync(formData);
        } catch {
          // onImageUploadError has already surfaced this to the user
        }
      }
      onClose();
    };

    uploadSequentially();
  }, [
    profileSaved,
    uploadsInitiated,
    isEditing,
    profile,
    createdProfileId,
    formState.primary_image,
    formState.secondary_image,
    formState.full_body_image,
    imageUploadMutation,
    secondaryImageUploadMutation,
    fullBodyImageUploadMutation,
    deleteProfilesMutation,
    onClose,
  ]);

  const valueUpdater = createValueUpdater(setFormState, setValidationState);

  const checkImageQuality = async (): Promise<ValidationResult> => {
    const checks = [
      {
        label: 'Primary',
        file: formState.primary_image,
        mutation: primaryQualityMutation,
      },
      {
        label: 'Secondary',
        file: formState.secondary_image,
        mutation: secondaryQualityMutation,
      },
    ].filter((check) => !!check.file);

    for (const check of checks) {
      const formData = new FormData();
      formData.append('file', check.file as File);
      formData.append('firstName', formState.first_name.trim());
      formData.append('lastName', formState.last_name.trim());
      try {
        await check.mutation.mutateAsync(formData);
      } catch (err) {
        return {
          isValid: false,
          message: `${check.label} image failed to meet the guidelines: ${
            (err as Error).message
          }`,
        };
      }
    }

    return { isValid: true, message: '' };
  };

  const handleSubmit = async () => {
    if (profileSaved) return;
    const validation = validateProfileForm(
      formState,
      formState.primary_image,
      isEditing,
      imagePreview,
      formState.secondary_image,
      formState.full_body_image
    );
    setValidationState(validation);

    if (!validation.isValid) {
      return;
    }

    const currentTenantId =
      isEditing && profile ? profile.tenantId || '' : tenantId || '';
    const tenantValidation = validateTenantAccess(user, currentTenantId);
    if (!tenantValidation.isValid) {
      setValidationState(tenantValidation);
      return;
    }

    setCheckingQuality(true);
    const qualityValidation = await checkImageQuality();
    setCheckingQuality(false);
    if (!qualityValidation.isValid) {
      setValidationState(qualityValidation);
      return;
    }

    const basePayload = {
      firstName: formState.first_name.trim(),
      lastName: formState.last_name.trim(),
      age: formState.age.trim() ? Number(formState.age) : 0,
      gender: formState.gender,
      employeeId: formState.employee_id.trim(),
      badgeId: formState.badge_id?.trim() || '',
      isEnabled: formState.is_enabled,
      profileType: formState.profile_type,
      tenantId: currentTenantId,
    };

    if (isEditing && profile) {
      const editPayload: UpdatePersonMatchProfilePayload = {
        ...basePayload,
        id: profile.id,
        primaryImageUrl:
          imagePreview === null ? '' : profile.primaryImageURL || '',
        secondaryImageUrl:
          secondaryImagePreview === null ? '' : profile.secondaryImageURL || '',
        fullBodyImageUrl:
          fullBodyImagePreview === null ? '' : profile.fullBodyImageURL || '',
      };

      editMutation.mutate(editPayload);
    } else {
      const addPayload = {
        ...basePayload,
        tags: [],
        miscInfo: {},
        primaryImageUrl: '',
      };
      addMutation.mutate(addPayload);
    }
  };

  const isLoading =
    checkingQuality ||
    imageUploadMutation.isLoading ||
    secondaryImageUploadMutation.isLoading ||
    fullBodyImageUploadMutation.isLoading ||
    (isEditing
      ? editMutation.isLoading
      : addMutation.isLoading || locationMappingMutation.isLoading);

  const hasChanges =
    isEditing && profile
      ? formState.first_name.trim() !== (profile.firstName || '') ||
        formState.last_name.trim() !== (profile.lastName || '') ||
        (formState.age.trim() ? Number(formState.age) : 0) !==
          (profile.age || 0) ||
        formState.gender !== profile.gender ||
        formState.employee_id.trim() !== (profile.employeeId || '') ||
        (formState.badge_id?.trim() || '') !== (profile.badgeId || '') ||
        formState.is_enabled !== (profile.isEnabled ?? false) ||
        formState.profile_type !== (profile.profileType || 'BLACKLISTED') ||
        !!formState.primary_image ||
        !!formState.secondary_image ||
        !!formState.full_body_image ||
        (imagePreview === null && !!profile.primaryImageURL) ||
        (secondaryImagePreview === null && !!profile.secondaryImageURL) ||
        (fullBodyImagePreview === null && !!profile.fullBodyImageURL)
      : true;

  const actions = (
    <>
      {!readOnly && (
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={isLoading || !hasChanges || profileSaved}
          loading={isLoading}
        >
          Submit
        </Button>
      )}
      <Button onClick={onClose} disabled={isLoading}>
        {readOnly ? 'Close' : 'Cancel'}
      </Button>
    </>
  );

  return (
    <Modal
      title={
        readOnly
          ? 'View Profile'
          : isEditing
          ? 'Edit Profile'
          : 'Add New Profile'
      }
      open={true}
      onClose={onClose}
      footer={actions}
    >
      <div className="w-full max-w-3xl space-y-4 px-8 py-6">
        <ModalAlerts
          validationState={validationState}
          mutation={isEditing ? editMutation : addMutation}
          imageUploadMutation={imageUploadMutation}
        />
        <ProfileFormFields
          formState={formState}
          valueUpdater={valueUpdater}
          showBadgeId={showBadgeId}
        />
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <ProfileImageUpload
            imagePreview={imagePreview}
            setImagePreview={setImagePreview}
            setFormState={setFormState}
            isRequired={true}
            setValidationState={setValidationState}
            label="Primary Image"
            imageType="primary"
          />
          <ProfileImageUpload
            imagePreview={secondaryImagePreview}
            setImagePreview={setSecondaryImagePreview}
            setFormState={setFormState}
            setValidationState={setValidationState}
            label="Secondary Image"
            imageType="secondary"
          />
          <ProfileImageUpload
            imagePreview={fullBodyImagePreview}
            setImagePreview={setFullBodyImagePreview}
            setFormState={setFormState}
            setValidationState={setValidationState}
            label="Full Body Image"
            imageType="full_body"
          />
        </div>
      </div>
    </Modal>
  );
}

export default AddEditProfileModal;
